import * as yup from "yup";
import { getTodayDateString } from "./scheduleHelpers";

export const SCHEDULE_TITLE_MIN = 3;
export const SCHEDULE_TITLE_MAX = 200;

/** Shared yup rule for schedule title (entry + edit forms). */
export function scheduleTitleSchema() {
  return yup
    .string()
    .required("Schedule title is required")
    .test(
      "not-only-spaces",
      "Schedule title cannot contain only spaces",
      (value) => value == null || value === "" || String(value).trim().length > 0,
    )
    .transform((value) => (value == null ? "" : String(value).trim()))
    .min(SCHEDULE_TITLE_MIN, `Schedule title must be at least ${SCHEDULE_TITLE_MIN} characters`)
    .max(SCHEDULE_TITLE_MAX, `Schedule title must not exceed ${SCHEDULE_TITLE_MAX} characters`);
}

export function getDefaultScheduleFormValues() {
  return {
    schedule_title: "",
    schedule_type_id: "",
    schedule_date: getTodayDateString(),
    schedule_time: "",
    location: "",
    description: "",
    assigned_to: "",
    assigned_to_name: "",
    priority_id: "",
    status_id: "",
    reminder: false,
    remarks: "",
  };
}

export function scheduleToFormValues(row) {
  if (!row) return getDefaultScheduleFormValues();
  return {
    schedule_title: row.schedule_title ?? "",
    schedule_type_id: row.schedule_type_id != null ? String(row.schedule_type_id) : "",
    schedule_date: row.schedule_date ? String(row.schedule_date).slice(0, 10) : "",
    schedule_time: row.schedule_time ? String(row.schedule_time).slice(0, 5) : "",
    location: row.location ?? "",
    description: row.description ?? "",
    assigned_to: row.assigned_to != null ? String(row.assigned_to) : "",
    assigned_to_name: row.assigned_to_name ?? "",
    priority_id: row.priority_id != null ? String(row.priority_id) : "",
    status_id: row.status_id != null ? String(row.status_id) : "",
    reminder: row.reminder === 1 || row.reminder === true,
    remarks: row.remarks ?? "",
  };
}

export function buildSchedulePayload(values) {
  return {
    schedule_title: String(values.schedule_title ?? "").trim(),
    schedule_type_id: Number(values.schedule_type_id),
    schedule_date: String(values.schedule_date ?? "").trim(),
    schedule_time: String(values.schedule_time ?? "").trim(),
    location: String(values.location ?? "").trim(),
    description: String(values.description ?? "").trim() || null,
    assigned_to: Number(values.assigned_to),
    priority_id: Number(values.priority_id),
    status_id: Number(values.status_id),
    reminder: Boolean(values.reminder),
    remarks: String(values.remarks ?? "").trim() || null,
  };
}

const requiredIdField = (message) =>
  yup
    .mixed()
    .required(message)
    .test("id", message, (v) => v !== "" && v != null && v !== undefined);

function buildScheduleSchema({ allowPastDate = false } = {}) {
  return yup.object({
    schedule_title: scheduleTitleSchema(),
    schedule_type_id: requiredIdField("Schedule type is required"),
    schedule_date: yup
      .string()
      .required("Date is required")
      .test("not-past", "Date cannot be in the past", (value) => {
        if (allowPastDate || !value) return true;
        return value >= getTodayDateString();
      }),
    schedule_time: yup.string().required("Time is required"),
    location: yup.string().trim().required("Location is required"),
    description: yup
      .string()
      .nullable()
      .transform((v) => (v == null ? "" : v)),
    assigned_to: requiredIdField(
      "Select a staff member from the search results (click the name or press Enter)",
    ),
    priority_id: requiredIdField("Priority is required"),
    status_id: requiredIdField("Status is required"),
    reminder: yup.boolean(),
    remarks: yup
      .string()
      .nullable()
      .transform((v) => (v == null ? "" : v)),
  });
}

export function buildScheduleCreateSchema() {
  return buildScheduleSchema({ allowPastDate: false });
}

export function buildScheduleEditSchema() {
  return buildScheduleSchema({ allowPastDate: true });
}
